/**
 * Nether terrain generator (worldgen for DimensionId 'nether').
 *
 * Vanilla-shaped, not a port: a 3D density field sampled on a coarse 4x8x4
 * grid and trilinearly interpolated (the same trick vanilla's NoiseChunk uses)
 * carves huge caverns out of netherrack between a bedrock floor (y 0..4) and a
 * bedrock roof (y 123..127). Open space at or below y = 31 is the lava sea.
 *
 * Biomes are picked from two low-frequency climate noises by nearest point in
 * (temperature, humidity), matching vanilla's multi-noise parameter points:
 *   nether wastes (0, 0), soul sand valley (0, -0.5), crimson forest (0.4, 0),
 *   warped forest (0, 0.5), basalt deltas (-0.5, 0).
 *
 * Passes: density -> bedrock -> surface (per biome) -> ores (quartz, nether
 * gold, magma, ancient debris) -> features (huge fungi, glowstone, basalt
 * pillars, deltas, lava falls). Every feature stays inside its own chunk so
 * the result is deterministic per chunk without neighbour data.
 *
 * Everything above the roof (y >= 128) is air, except a bedrock cap on the
 * top layer of the chunk column, which keeps the sky-light pass from lighting
 * anything: the Nether has no sky light at all.
 */

import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import {
  AIR, ANCIENT_DEBRIS_ID, BASALT_ID, BEDROCK_ID, BLACKSTONE_ID, CRIMSON_NYLIUM_ID, CRIMSON_STEM_ID,
  GLOWSTONE_ID, GRAVEL_ID, LAVA_ID, MAGMA_ID, NETHER_GOLD_ORE_ID, NETHER_WART_BLOCK_ID, NETHERRACK_ID,
  QUARTZ_ORE_ID, SHROOMLIGHT_ID, SOUL_SAND_ID, SOUL_SOIL_ID, WARPED_NYLIUM_ID, WARPED_STEM_ID,
  WARPED_WART_BLOCK_ID, CRIMSON_ROOTS_ID, WARPED_ROOTS_ID, CRIMSON_FUNGUS_ID, WARPED_FUNGUS_ID,
  WEEPING_VINES_ID, WEEPING_VINES_PLANT_ID, TWISTING_VINES_ID, TWISTING_VINES_PLANT_ID,
} from '../blocks';
import { placeFortress } from './netherFortress';
import { placeNetherStructures } from './netherStructures';
import { FALLING_LAVA_ID } from '../blocks';
import { hash2, mulberry32, Simplex } from '../noise';
import { NETHER } from '../world/dimension';

export enum NetherBiome {
  Wastes = 0,
  SoulSandValley = 1,
  CrimsonForest = 2,
  WarpedForest = 3,
  BasaltDeltas = 4,
}

export const NETHER_BIOME_NAMES: Record<NetherBiome, string> = {
  [NetherBiome.Wastes]: 'nether_wastes',
  [NetherBiome.SoulSandValley]: 'soul_sand_valley',
  [NetherBiome.CrimsonForest]: 'crimson_forest',
  [NetherBiome.WarpedForest]: 'warped_forest',
  [NetherBiome.BasaltDeltas]: 'basalt_deltas',
};

/** (temperature, humidity) parameter points, vanilla MultiNoiseBiomeSourceParameterList.NETHER. */
const BIOME_POINTS: Array<[NetherBiome, number, number]> = [
  [NetherBiome.Wastes, 0, 0],
  [NetherBiome.SoulSandValley, 0, -0.5],
  [NetherBiome.CrimsonForest, 0.4, 0],
  [NetherBiome.WarpedForest, 0, 0.5],
  [NetherBiome.BasaltDeltas, -0.5, 0],
];

export const NETHER_HEIGHT = NETHER.height; // 128
export const LAVA_SEA = NETHER.seaLevel; // 31
const ROOF = NETHER_HEIGHT - 1; // 127

// coarse density grid: 4 x 8 x 4 cells
const CW = 4;
const CH = 8;
const NX = CHUNK_SIZE / CW + 1; // 5
const NY = NETHER_HEIGHT / CH + 1; // 17
const AREA = CHUNK_SIZE * CHUNK_SIZE;

export class NetherGenerator {
  readonly seed: number;
  private readonly n1: Simplex;
  private readonly n2: Simplex;
  private readonly n3: Simplex;
  private readonly temp: Simplex;
  private readonly humid: Simplex;
  private readonly surf: Simplex;
  private readonly grid = new Float32Array(NX * NY * NX);
  private readonly biomes = new Uint8Array(AREA);

  constructor(seed: number) {
    this.seed = seed | 0;
    const s = this.seed ^ 0x4e455448; // 'NETH': independent of the overworld noises
    this.n1 = new Simplex(s + 1);
    this.n2 = new Simplex(s + 2);
    this.n3 = new Simplex(s + 3);
    this.temp = new Simplex(s + 11);
    this.humid = new Simplex(s + 12);
    this.surf = new Simplex(s + 21);
  }

  /** Biome of a column (biomes are 2D here; vanilla's are 3D but rarely vary with y). */
  biomeAt(x: number, z: number): NetherBiome {
    // a little warp keeps the borders from looking like noise isolines
    const wx = x + this.surf.noise2(x * 0.02, z * 0.02) * 12;
    const wz = z + this.surf.noise2(x * 0.02 + 50, z * 0.02 + 50) * 12;
    const t = this.temp.noise2(wx / 380, wz / 380) * 0.9;
    const h = this.humid.noise2(wx / 380, wz / 380) * 0.9;
    let best = NetherBiome.Wastes;
    let bestD = Infinity;
    for (const [b, bt, bh] of BIOME_POINTS) {
      const d = (t - bt) * (t - bt) + (h - bh) * (h - bh);
      if (d < bestD) { bestD = d; best = b; }
    }
    return best;
  }

  /** Raw terrain density at a world point (> 0 = solid), before bedrock/surface. */
  density(x: number, y: number, z: number): number {
    let d = this.n1.noise3(x / 96, y / 44, z / 96) * 0.62
      + this.n2.noise3(x / 38, y / 22, z / 38) * 0.3
      + this.n3.noise3(x / 15, y / 11, z / 15) * 0.12;
    // solid floor under the lava sea and a thick roof
    if (y < 24) d += ((24 - y) / 24) * 1.4;
    if (y > 96) d += ((y - 96) / (ROOF - 96)) * 1.7;
    return d - 0.08;
  }

  generateChunkBlocks(cx: number, cz: number, out: Uint16Array): void {
    out.fill(AIR);
    const ox = cx * CHUNK_SIZE;
    const oz = cz * CHUNK_SIZE;
    const rnd = mulberry32((Math.imul(cx, 341873128) ^ Math.imul(cz, 132897987) ^ this.seed) | 0);

    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) this.biomes[x + z * CHUNK_SIZE] = this.biomeAt(ox + x, oz + z);
    }

    this.fillDensity(ox, oz, out);
    this.placeBedrock(cx, cz, out);
    this.applySurface(ox, oz, out);
    this.placeOres(rnd, out);
    this.placeFeatures(rnd, ox, oz, out);
    this.vegetation(ox, oz, out);
    placeFortress(this.seed, cx, cz, out);
    placeNetherStructures(this.seed, cx, cz, out);
  }

  private fillDensity(ox: number, oz: number, out: Uint16Array): void {
    const g = this.grid;
    for (let iz = 0; iz < NX; iz++) {
      for (let iy = 0; iy < NY; iy++) {
        for (let ix = 0; ix < NX; ix++) {
          g[ix + iy * NX + iz * NX * NY] = this.density(ox + ix * CW, iy * CH, oz + iz * CW);
        }
      }
    }
    for (let z = 0; z < CHUNK_SIZE; z++) {
      const iz = (z / CW) | 0;
      const fz = (z % CW) / CW;
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const ix = (x / CW) | 0;
        const fx = (x % CW) / CW;
        const biome = this.biomes[x + z * CHUNK_SIZE];
        for (let y = 1; y < ROOF; y++) {
          const iy = (y / CH) | 0;
          const fy = (y % CH) / CH;
          const i000 = ix + iy * NX + iz * NX * NY;
          const c00 = g[i000] + (g[i000 + 1] - g[i000]) * fx;
          const c10 = g[i000 + NX] + (g[i000 + NX + 1] - g[i000 + NX]) * fx;
          const i001 = i000 + NX * NY;
          const c01 = g[i001] + (g[i001 + 1] - g[i001]) * fx;
          const c11 = g[i001 + NX] + (g[i001 + NX + 1] - g[i001 + NX]) * fx;
          const c0 = c00 + (c10 - c00) * fy;
          const c1 = c01 + (c11 - c01) * fy;
          const d = c0 + (c1 - c0) * fz;
          const i = x + z * CHUNK_SIZE + y * AREA;
          if (d > 0) out[i] = biome === NetherBiome.BasaltDeltas ? this.deltaStone(ox + x, y, oz + z) : NETHERRACK_ID;
          else if (y <= LAVA_SEA) out[i] = LAVA_ID;
        }
      }
    }
  }

  /** Basalt deltas: basalt with veins of blackstone. */
  private deltaStone(x: number, y: number, z: number): number {
    return this.n3.noise3(x / 9, y / 9, z / 9) > 0.35 ? BLACKSTONE_ID : BASALT_ID;
  }

  private placeBedrock(cx: number, cz: number, out: Uint16Array): void {
    const s = this.seed ^ 0x5bd1e995;
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const col = x + z * CHUNK_SIZE;
        const wx = cx * CHUNK_SIZE + x, wz = cz * CHUNK_SIZE + z;
        out[col] = BEDROCK_ID;
        out[col + ROOF * AREA] = BEDROCK_ID;
        // vanilla: chance falls off linearly over 5 layers
        for (let k = 1; k <= 4; k++) {
          if (hash2(wx * 7 + k, wz, s) < (5 - k) / 5) out[col + k * AREA] = BEDROCK_ID;
          if (hash2(wx, wz * 7 + k, s + 1) < (5 - k) / 5) out[col + (ROOF - k) * AREA] = BEDROCK_ID;
        }
        // top-of-column cap: blocks sky light (see header)
        out[col + (WORLD_HEIGHT - 1) * AREA] = BEDROCK_ID;
      }
    }
  }

  private applySurface(ox: number, oz: number, out: Uint16Array): void {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const wx = ox + x, wz = oz + z;
        const biome = this.biomes[x + z * CHUNK_SIZE];
        const col = x + z * CHUNK_SIZE;
        const patch = this.surf.noise2(wx / 14, wz / 14);
        const soil = this.surf.noise3(wx / 7, 0, wz / 7) > 0;
        for (let y = ROOF - 1; y > 4; y--) {
          const i = col + y * AREA;
          const id = out[i];
          if (id !== NETHERRACK_ID) continue;
          const above = out[i + AREA];
          if (above !== AIR && above !== LAVA_ID) continue;
          if (above === LAVA_ID) continue; // lava sea bed stays netherrack
          // a floor: air above
          switch (biome) {
            case NetherBiome.CrimsonForest:
              out[i] = CRIMSON_NYLIUM_ID;
              break;
            case NetherBiome.WarpedForest:
              out[i] = WARPED_NYLIUM_ID;
              break;
            case NetherBiome.SoulSandValley: {
              const top = soil ? SOUL_SOIL_ID : SOUL_SAND_ID;
              for (let k = 0; k < 3 && out[i - k * AREA] === NETHERRACK_ID; k++) out[i - k * AREA] = k === 0 ? top : SOUL_SOIL_ID;
              break;
            }
            case NetherBiome.Wastes:
              // gravel and soul sand beaches along the lava sea, soul sand patches elsewhere
              if (y >= LAVA_SEA - 1 && y <= LAVA_SEA + 3 && patch > 0.15) out[i] = GRAVEL_ID;
              else if (y >= LAVA_SEA - 1 && y <= LAVA_SEA + 3 && patch < -0.35) out[i] = SOUL_SAND_ID;
              else if (patch > 0.62) out[i] = SOUL_SAND_ID;
              break;
            default:
              break;
          }
        }
      }
    }
  }

  // --- ores ------------------------------------------------------------------

  private placeOres(rnd: () => number, out: Uint16Array): void {
    // vanilla counts per chunk: quartz 16 x 14, nether gold 10 x 10, magma 4 x 33
    // (y 27..36)
    for (let i = 0; i < 16; i++) this.vein(rnd, out, QUARTZ_ORE_ID, 14, 10, 117);
    for (let i = 0; i < 10; i++) this.vein(rnd, out, NETHER_GOLD_ORE_ID, 10, 10, 117);
    for (let i = 0; i < 4; i++) this.vein(rnd, out, MAGMA_ID, 33, 27, 36);
    // ancient debris (minecraft.wiki): A = 0..3 attempts, triangular over y 8..22 (peak 15);
    // B = 0..2 attempts uniform over y 8..119; veins of 1..3, never air-exposed
    const a = (rnd() * 4) | 0;
    for (let i = 0; i < a; i++) {
      const y = Math.round(8 + (rnd() + rnd()) * 7); // triangular 8..22, peak 15
      this.vein(rnd, out, ANCIENT_DEBRIS_ID, 1 + ((rnd() * 3) | 0), y, y, true);
    }
    const b = (rnd() * 3) | 0;
    for (let i = 0; i < b; i++) this.vein(rnd, out, ANCIENT_DEBRIS_ID, 1 + ((rnd() * 3) | 0), 8, 119, true);
  }

  /** A random-walk blob of `size` blocks replacing netherrack (debris: hidden only). */
  private vein(rnd: () => number, out: Uint16Array, id: number, size: number, y0: number, y1: number, hidden = false): void {
    let x = 1 + ((rnd() * 14) | 0);
    let z = 1 + ((rnd() * 14) | 0);
    let y = y0 + ((rnd() * (y1 - y0 + 1)) | 0);
    for (let n = 0; n < size; n++) {
      if (x >= 0 && x < CHUNK_SIZE && z >= 0 && z < CHUNK_SIZE && y > 4 && y < ROOF - 4) {
        const i = x + z * CHUNK_SIZE + y * AREA;
        if (out[i] === NETHERRACK_ID && (!hidden || !this.exposed(out, x, y, z))) out[i] = id;
      }
      const r = (rnd() * 6) | 0;
      if (r === 0) x++; else if (r === 1) x--; else if (r === 2) z++; else if (r === 3) z--;
      else if (r === 4) y++; else y--;
    }
  }

  /** Does the cell touch air? (ancient debris is air-exposure-discarded in vanilla) */
  private exposed(out: Uint16Array, x: number, y: number, z: number): boolean {
    const i = x + z * CHUNK_SIZE + y * AREA;
    if (out[i + AREA] === AIR || out[i - AREA] === AIR) return true;
    if (x > 0 && out[i - 1] === AIR) return true;
    if (x < 15 && out[i + 1] === AIR) return true;
    if (z > 0 && out[i - CHUNK_SIZE] === AIR) return true;
    if (z < 15 && out[i + CHUNK_SIZE] === AIR) return true;
    return false;
  }

  // --- features ----------------------------------------------------------------

  private placeFeatures(rnd: () => number, ox: number, oz: number, out: Uint16Array): void {
    const centre = this.biomes[8 + 8 * CHUNK_SIZE];
    // glowstone: vanilla 10 biased + 10 uniform attempts; fewer, fuller clusters here
    const glow = centre === NetherBiome.BasaltDeltas || centre === NetherBiome.SoulSandValley ? 2 : 4;
    for (let i = 0; i < glow; i++) this.glowstone(rnd, out);

    for (let i = 0; i < 4; i++) this.lavaFall(rnd, out);

    if (centre === NetherBiome.CrimsonForest || centre === NetherBiome.WarpedForest) {
      for (let i = 0; i < 8; i++) this.fungus(rnd, out);
    }
    if (centre === NetherBiome.BasaltDeltas) this.deltas(rnd, ox, oz, out);
    if (centre === NetherBiome.SoulSandValley) {
      for (let i = 0; i < 2; i++) this.basaltPillar(rnd, out);
    }
  }

  /** Random floor y (solid `id` with air above) in column (x, z), or -1. */
  private floorIn(rnd: () => number, out: Uint16Array, x: number, z: number, accept: (id: number) => boolean): number {
    const ys: number[] = [];
    for (let y = LAVA_SEA; y < ROOF - 6; y++) {
      const i = x + z * CHUNK_SIZE + y * AREA;
      if (accept(out[i]) && out[i + AREA] === AIR) ys.push(y);
    }
    return ys.length ? ys[(rnd() * ys.length) | 0] : -1;
  }

  private glowstone(rnd: () => number, out: Uint16Array): void {
    const x = 4 + ((rnd() * 8) | 0);
    const z = 4 + ((rnd() * 8) | 0);
    // a ceiling: air with netherrack directly above
    const ys: number[] = [];
    for (let y = 8; y < ROOF - 2; y++) {
      const i = x + z * CHUNK_SIZE + y * AREA;
      if (out[i] === AIR && out[i + AREA] === NETHERRACK_ID) ys.push(y);
    }
    if (!ys.length) return;
    const y0 = ys[(rnd() * ys.length) | 0];
    out[x + z * CHUNK_SIZE + y0 * AREA] = GLOWSTONE_ID;
    for (let n = 0; n < 140; n++) {
      const px = x + ((rnd() * 4) | 0) - ((rnd() * 4) | 0);
      const pz = z + ((rnd() * 4) | 0) - ((rnd() * 4) | 0);
      const py = y0 - ((rnd() * 6) | 0);
      if (px < 0 || px > 15 || pz < 0 || pz > 15 || py < 5) continue;
      const i = px + pz * CHUNK_SIZE + py * AREA;
      if (out[i] !== AIR) continue;
      let touching = 0;
      if (out[i + AREA] === GLOWSTONE_ID) touching++;
      if (out[i - AREA] === GLOWSTONE_ID) touching++;
      if (px > 0 && out[i - 1] === GLOWSTONE_ID) touching++;
      if (px < 15 && out[i + 1] === GLOWSTONE_ID) touching++;
      if (pz > 0 && out[i - CHUNK_SIZE] === GLOWSTONE_ID) touching++;
      if (pz < 15 && out[i + CHUNK_SIZE] === GLOWSTONE_ID) touching++;
      if (touching === 1) out[i] = GLOWSTONE_ID;
    }
  }

  /** A lava source in a wall with a (static) fall down to the floor. */
  private lavaFall(rnd: () => number, out: Uint16Array): void {
    for (let attempt = 0; attempt < 24; attempt++) {
      const x = 1 + ((rnd() * 14) | 0);
      const z = 1 + ((rnd() * 14) | 0);
      const y = LAVA_SEA + 4 + ((rnd() * 80) | 0);
      const i = x + z * CHUNK_SIZE + y * AREA;
      if (out[i] !== NETHERRACK_ID || out[i + AREA] !== NETHERRACK_ID || out[i - AREA] !== NETHERRACK_ID) continue;
      const opens = [1, -1, CHUNK_SIZE, -CHUNK_SIZE].filter((d) => out[i + d] === AIR);
      if (opens.length !== 1) continue;
      out[i] = LAVA_ID;
      let j = i + opens[0];
      let yy = y;
      // the fall: lava cells down the open face until something solid
      const lx = j % CHUNK_SIZE, lz = ((j / CHUNK_SIZE) | 0) % CHUNK_SIZE;
      if (lx < 0 || lx > 15 || lz < 0 || lz > 15) return;
      out[j] = FALLING_LAVA_ID;
      while (yy > 5 && out[j - AREA] === AIR) {
        j -= AREA;
        yy--;
        out[j] = FALLING_LAVA_ID;
      }
      return;
    }
  }

  /** Huge crimson / warped fungus: a stem with a wart-block hat and shroomlights. */
  private fungus(rnd: () => number, out: Uint16Array): void {
    const x = 3 + ((rnd() * 10) | 0);
    const z = 3 + ((rnd() * 10) | 0);
    const y = this.floorIn(rnd, out, x, z, (id) => id === CRIMSON_NYLIUM_ID || id === WARPED_NYLIUM_ID);
    if (y < 0) return;
    const ground = out[x + z * CHUNK_SIZE + y * AREA];
    const crimson = ground === CRIMSON_NYLIUM_ID;
    const stem = crimson ? CRIMSON_STEM_ID : WARPED_STEM_ID;
    const wart = crimson ? NETHER_WART_BLOCK_ID : WARPED_WART_BLOCK_ID;
    let h = 4 + ((rnd() * 9) | 0); // vanilla 4..13
    // shrink to fit the headroom
    let room = 0;
    while (room < h + 2 && y + 1 + room < ROOF && out[x + z * CHUNK_SIZE + (y + 1 + room) * AREA] === AIR) room++;
    if (room < 5) return;
    h = Math.min(h, room - 2);
    for (let k = 1; k <= h; k++) out[x + z * CHUNK_SIZE + (y + k) * AREA] = stem;
    const top = y + h;
    const hatH = Math.max(2, Math.min(h - 1, 2 + ((rnd() * 3) | 0)));
    for (let dy = -hatH + 1; dy <= 1; dy++) {
      const yy = top + dy;
      const r = dy === 1 ? 1 : 2;
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          const px = x + dx, pz = z + dz;
          if (px < 0 || px > 15 || pz < 0 || pz > 15 || yy >= ROOF) continue;
          const edge = Math.abs(dx) === r || Math.abs(dz) === r;
          // the hat is a shell: only its rim below the top layer, ragged at the corners
          if (dy < 1 && !edge && dy < 0) continue;
          if (Math.abs(dx) === r && Math.abs(dz) === r && rnd() < 0.6) continue;
          const i = px + pz * CHUNK_SIZE + yy * AREA;
          if (out[i] !== AIR) continue;
          out[i] = rnd() < 0.08 ? SHROOMLIGHT_ID : wart;
        }
      }
    }
  }

  /** Forest floor cover: roots and fungi on nylium, weeping vines under crimson
   *  ceilings, twisting vines up from warped nylium. Per-column hashes. */
  private vegetation(ox: number, oz: number, out: Uint16Array): void {
    const s = this.seed ^ 0x7e9e;
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const wx = ox + x, wz = oz + z;
        const col = x + z * CHUNK_SIZE;
        for (let y = LAVA_SEA + 1; y < ROOF - 2; y++) {
          const i = col + y * AREA;
          const id = out[i];
          if (out[i + AREA] === AIR && (id === CRIMSON_NYLIUM_ID || id === WARPED_NYLIUM_ID)) {
            const r = hash2(wx * 7 + y, wz * 13 - y, s);
            const crimson = id === CRIMSON_NYLIUM_ID;
            if (r < 0.14) out[i + AREA] = crimson ? CRIMSON_ROOTS_ID : WARPED_ROOTS_ID;
            else if (r < 0.17) out[i + AREA] = crimson ? CRIMSON_FUNGUS_ID : WARPED_FUNGUS_ID;
            else if (!crimson && r < 0.19) {
              const len = 1 + Math.floor(hash2(wx, wz + y, s + 1) * 8);
              let k = 1;
              for (; k <= len && out[i + k * AREA] === AIR && y + k < ROOF - 1; k++) out[i + k * AREA] = TWISTING_VINES_PLANT_ID;
              if (k > 1) out[i + (k - 1) * AREA] = TWISTING_VINES_ID;
            }
          } else if (id === AIR && (out[i + AREA] === NETHER_WART_BLOCK_ID || (out[i + AREA] === NETHERRACK_ID && this.biomes[col] === NetherBiome.CrimsonForest))) {
            const r = hash2(wx * 5 - y, wz * 3 + y, s + 2);
            if (r < (out[i + AREA] === NETHER_WART_BLOCK_ID ? 0.2 : 0.04)) {
              const len = 1 + Math.floor(hash2(wx + y, wz, s + 3) * 8);
              let k = 0;
              for (; k < len && out[i - k * AREA] === AIR && y - k > LAVA_SEA + 1; k++) out[i - k * AREA] = WEEPING_VINES_PLANT_ID;
              if (k > 0) out[i - (k - 1) * AREA] = WEEPING_VINES_ID;
            }
          }
        }
      }
    }
  }

  /** Basalt deltas: lava/magma pools in the floor and short basalt columns. */
  private deltas(rnd: () => number, ox: number, oz: number, out: Uint16Array): void {
    for (let z = 1; z < 15; z++) {
      for (let x = 1; x < 15; x++) {
        const n = this.surf.noise2((ox + x) / 6, (oz + z) / 6);
        for (let y = LAVA_SEA + 1; y < ROOF - 6; y++) {
          const i = x + z * CHUNK_SIZE + y * AREA;
          const id = out[i];
          if ((id !== BASALT_ID && id !== BLACKSTONE_ID) || out[i + AREA] !== AIR) continue;
          if (n > 0.45 && out[i - AREA] !== AIR && out[i - 1] !== AIR && out[i + 1] !== AIR
            && out[i - CHUNK_SIZE] !== AIR && out[i + CHUNK_SIZE] !== AIR) {
            out[i] = LAVA_ID;
            out[i - 1] = out[i - 1] === LAVA_ID ? LAVA_ID : MAGMA_ID;
          } else if (n > 0.3) {
            out[i] = MAGMA_ID;
          } else if (rnd() < 0.02) {
            const h = 1 + ((rnd() * 4) | 0);
            for (let k = 1; k <= h && out[i + k * AREA] === AIR; k++) out[i + k * AREA] = BASALT_ID;
          }
        }
      }
    }
  }

  /** Soul sand valley basalt pillar: floor to ceiling where the gap is short enough. */
  private basaltPillar(rnd: () => number, out: Uint16Array): void {
    const x = 2 + ((rnd() * 12) | 0);
    const z = 2 + ((rnd() * 12) | 0);
    const y = this.floorIn(rnd, out, x, z, (id) => id === SOUL_SAND_ID || id === SOUL_SOIL_ID || id === NETHERRACK_ID);
    if (y < 0) return;
    let top = y + 1;
    while (top < ROOF && out[x + z * CHUNK_SIZE + top * AREA] === AIR) top++;
    if (top >= ROOF - 1 || top - y > 30) return;
    for (let yy = y + 1; yy < top; yy++) {
      out[x + z * CHUNK_SIZE + yy * AREA] = BASALT_ID;
      // thicker at the base and top
      if (yy - y < 3 || top - yy < 3) {
        for (const d of [1, -1, CHUNK_SIZE, -CHUNK_SIZE]) {
          const j = x + z * CHUNK_SIZE + yy * AREA + d;
          if (out[j] === AIR && rnd() < 0.7) out[j] = BASALT_ID;
        }
      }
    }
  }
}
